import { FileApplication } from '@/application/file/file';
import type {
  TCacheStreamParams,
  TFetchMissingUsersParams,
  TFetchStreamParams,
  TGetOrFetchStreamHeadParams,
  TMissingPostsParams,
  TPartialCacheHitParams,
  TPersistUnreadNewStreamChunkParams,
  TPostStreamChunkResponse,
} from '@/application/stream/posts/post.types';
import { COLLECTIONS_DISCOVER_MAX_FETCHES_PER_LOAD } from '@/config/collections';
import { getStreamCacheMaxAgeMs } from '@/config/nexus';
import { CONTENT_SEARCH_MAX_SKIP } from '@/config/search';
import {
  FORCE_FETCH_NEW_POSTS,
  NOT_FOUND_CACHED_STREAM,
  SKIP_FETCH_NEW_POSTS,
} from '@/controllers/stream/posts/post.constants';
import type {
  TClearUnreadStreamParams,
  TMarkUnreadPostsAsReadParams,
  TStreamIdParams,
} from '@/controllers/stream/posts/posts.types';
import { Logger } from '@/libs/logger/logger';
import { parseCollectionContent } from '@/libs/post/collectionContent';
import { BookmarkModel } from '@/models/bookmark/bookmark';
import { CompositeIdDomain, type Pubky } from '@/models/models.types';
import { buildCompositeId, buildCompositeIdFromPubkyUri, parseCompositeId } from '@/models/models.utils';
import { PostDetailsModel } from '@/models/post/details/postDetails';
import { DELETED } from '@/models/post/details/postDetails.constants';
import type { PostRelationshipsModelSchema } from '@/models/post/relationships/postRelationships.schema';
import {
  isAuthorScopedContentSearchStream,
  isAuthorStreamSkippingMuteFilter,
  isBookmarkStream,
  isContentSearchStream,
  isDeletedRetainingStream,
  isDiscoverCollectionsStream,
  isSkipPaginatedStream,
  parseContentSearchStreamId,
  type PostStreamId,
} from '@/models/stream/post/postStream.types';
import { PostStreamModel } from '@/models/stream/post/tables/postStream';
import { UnreadPostStreamModel } from '@/models/stream/post/tables/postStream.unread';
import { UserStreamTypes } from '@/models/stream/user/userStream.types';
import { LocalPostService } from '@/services/local/post/post';
import type { TStreamResult } from '@/services/local/stream/posts/post.types';
import { LocalStreamPostsService } from '@/services/local/stream/posts/posts';
import { postStreamDirtyRegistry } from '@/services/local/stream/posts/postStreamDirtyRegistry';
import { LocalStreamUsersService } from '@/services/local/stream/users/users';
import { LocalTagCacheService } from '@/services/local/tag/tag-cache';
import { NexusPostStreamService } from '@/services/nexus/stream/posts/postStream';
import { StreamKind, StreamOrder, StreamSource } from '@/services/nexus/stream/posts/postStream.types';
import { breakDownStreamId, createPostStreamParams } from '@/services/nexus/stream/posts/postStream.utils';
import { NexusUserStreamService } from '@/services/nexus/stream/users/userStream';
import { MuteFilter } from './muting/mute-filter';
import { postStreamQueue } from './muting/post-stream-queue';

export class PostStreamApplication {
  private constructor() {}

  private static readonly unreadHydrations = new Map<string, { request: Promise<boolean>; isCurrent: () => boolean }>();

  // ============================================================================
  // Public API
  // ============================================================================

  static async getUnreadStream({ streamId }: TStreamIdParams): Promise<TStreamResult | null> {
    return await LocalStreamPostsService.readUnreadStream({ streamId });
  }

  /**
   * The Nexus position a fresh pagination session resumes from once the cached ids are
   * exhausted (see `resolveCachedStreamTailCursor`), or `NOT_FOUND_CACHED_STREAM` when
   * there is no usable cache.
   */
  static async getCachedLastPostTimestamp({ streamId }: TStreamIdParams): Promise<number> {
    try {
      const postStream = await LocalStreamPostsService.read({ streamId });
      if (!postStream || postStream.stream.length === 0) {
        Logger.warn('StreamId not found in cache', { streamId });
        return NOT_FOUND_CACHED_STREAM;
      }

      return (await this.resolveCachedStreamTailCursor(streamId, postStream)) ?? NOT_FOUND_CACHED_STREAM;
    } catch (error) {
      Logger.warn('Failed to get timeline initial cursor', { streamId, error });
      return NOT_FOUND_CACHED_STREAM;
    }
  }

  /**
   * Resolve the Nexus cursor a cached stream resumes from at the cache→Nexus seam.
   *
   * Nexus keeps a post at its original stream position (its score) but bumps the post's
   * `indexed_at` when it is edited or deleted, so a cursor derived from `indexed_at` can
   * land above posts that are already cached; the seam then re-serves them and, with the
   * raw anchor sitting on one of them, never advances (#2523, #1569). The only exact
   * cursor is the `last_post_score` Nexus returned for the deepest page, persisted on the
   * row as `tailCursor`. Rows without one (written before it was tracked, or seeded by
   * bootstrap) fall back once to the last resolvable post timestamp — bookmark time for
   * bookmark streams — and their first Nexus page persists the real cursor.
   */
  private static async resolveCachedStreamTailCursor(
    streamId: PostStreamId,
    cachedStream: TStreamResult,
  ): Promise<number | undefined> {
    if (cachedStream.tailCursor !== undefined) {
      return cachedStream.tailCursor;
    }

    // Iterate backwards through the stream to find the last entry we can resolve
    // a pagination cursor for. This handles cases where the last PostDetails (or
    // bookmark row) might be missing.
    for (let i = cachedStream.stream.length - 1; i >= 0; i--) {
      const cursor = await this.getStreamCursorTimestamp(streamId, cachedStream.stream[i]);

      if (cursor !== undefined) {
        return cursor;
      }
    }

    // No stream entry yielded a cursor, cache is not useful
    Logger.warn('No cursor found in cached stream', { streamId, streamLength: cachedStream.stream.length });
    return undefined;
  }

  /**
   * Gets the head of the stream
   * @param params - The parameters for the stream
   * @returns The postId of the head of the stream
   */
  static async getStreamHead(params: TStreamIdParams): Promise<number> {
    return await LocalStreamPostsService.getStreamHead(params);
  }

  /**
   * Retry unread details without delaying newer-key discovery when a cursor is already cached.
   * The poll starts from the first row entry that resolves a timestamp: the unread head, else the
   * first main-row id with cached details. An id without details therefore never stalls the poll
   * (#2608), whether it is still unread or sits at the main head because the post above it was
   * removed.
   */
  static async getOrFetchStreamHead({ streamId, viewerId, isCurrent }: TGetOrFetchStreamHeadParams): Promise<number> {
    const unreadStream = await this.getUnreadStream({ streamId });
    let hydration: Promise<boolean> | undefined;
    if (unreadStream && unreadStream.stream.length > 0) {
      const cacheMissPostIds = await this.getNotPersistedPostsInCache(unreadStream.stream);
      if (cacheMissPostIds.length > 0) {
        hydration = this.hydrateUnreadPosts({ streamId, cacheMissPostIds, viewerId, isCurrent });
      }
    }

    const readHead = async () => {
      const streamHead = await this.getStreamHead({ streamId });
      // Nexus may omit a deleted or not-yet-indexed unread ID. Keep it pending
      // for hydration, but let newer keys arrive from the known main-stream cursor.
      return streamHead === SKIP_FETCH_NEW_POSTS ? this.getResolvableMainStreamHeadTimestamp({ streamId }) : streamHead;
    };
    if (!isCurrent()) return SKIP_FETCH_NEW_POSTS;
    let streamHead = await readHead();
    if (hydration && streamHead <= FORCE_FETCH_NEW_POSTS) {
      // Without a known cursor, hydration may be the only way to establish one.
      // Re-read after it settles: an initial load may have replaced these rows.
      await hydration;
      if (!isCurrent()) return SKIP_FETCH_NEW_POSTS;
      streamHead = await readHead();
    }
    return isCurrent() ? streamHead : SKIP_FETCH_NEW_POSTS;
  }

  private static hydrateUnreadPosts({
    streamId,
    cacheMissPostIds,
    viewerId,
    isCurrent,
  }: TGetOrFetchStreamHeadParams & { cacheMissPostIds: string[] }): Promise<boolean> {
    if (!isCurrent()) return Promise.resolve(false);
    const key = JSON.stringify([streamId, viewerId]);
    const pending = this.unreadHydrations.get(key);
    if (pending?.isCurrent()) return pending.request;

    // The hydration helper handles failures and leaves missing IDs retryable.
    // Share the whole persist, not only the underlying Nexus request.
    const request = this.fetchMissingPostsFromNexus({ cacheMissPostIds, viewerId, isCurrent }).finally(() => {
      if (this.unreadHydrations.get(key)?.request === request) this.unreadHydrations.delete(key);
    });
    this.unreadHydrations.set(key, { request, isCurrent });
    return request;
  }

  /**
   * Get local stream data from cache
   * @param streamId - The ID of the stream
   * @returns The cached stream or null if not found
   */
  static async getLocalStream({ streamId }: TStreamIdParams): Promise<TStreamResult | null> {
    return await LocalStreamPostsService.read({ streamId });
  }

  static async mergeUnreadStreamWithPostStream(params: TStreamIdParams) {
    return await LocalStreamPostsService.mergeUnreadStreamWithPostStream(params);
  }

  static async clearUnreadStream(params: TClearUnreadStreamParams): Promise<string[]> {
    return await LocalStreamPostsService.clearUnreadStream(params);
  }

  static async markUnreadPostsAsRead(params: TMarkUnreadPostsAsReadParams): Promise<void> {
    await LocalStreamPostsService.markUnreadPostsAsRead(params);
  }

  /**
   * Filter out deleted posts from a list of post IDs.
   * Posts without details in cache are kept (fail-open semantics).
   *
   * @param postIds - Array of post IDs to filter
   * @returns Array of post IDs that are not deleted
   */
  static async filterDeletedPosts(postIds: string[]): Promise<string[]> {
    return LocalPostService.filterDeletedPosts(postIds);
  }

  static async filterStreamPosts({
    streamId,
    postIds,
    strictReplyClassification = false,
  }: {
    streamId: PostStreamId;
    postIds: string[];
    strictReplyClassification?: boolean;
  }): Promise<string[]> {
    // Bookmark and single-collection item feeds keep deleted posts so the post
    // component can render its deleted-state placeholder; all other streams drop them.
    const visiblePostIds = isDeletedRetainingStream(streamId)
      ? postIds
      : await LocalPostService.filterDeletedPosts(postIds);
    const afterCollections = await this.filterCollectionsFromStream({ streamId, postIds: visiblePostIds });
    const afterReplies = await this.filterRepliesFromAuthorScopedSearch({
      streamId,
      postIds: afterCollections,
      strict: strictReplyClassification,
    });
    // Discover drops empty collections (nothing to discover). Lives here so it is re-applied by
    // the controller's post-hydration second pass, catching ids that were fail-open on details.
    return isDiscoverCollectionsStream(streamId) ? this.filterEmptyCollections(afterReplies) : afterReplies;
  }

  /**
   * Profile "Filter posts" (author-scoped content search) excludes replies, but the by_content
   * endpoint cannot exclude them server-side. Classify via the local relationships row: a post
   * with `replied !== null` is a reply. Pre-hydration (`strict = false`) posts without a
   * relationships row are kept so the cache-miss hydration can classify them; post-hydration
   * (`strict = true`) unclassifiable posts are dropped — degraded mode hides a result rather
   * than ever rendering a possible reply on the Posts tab.
   */
  private static async filterRepliesFromAuthorScopedSearch({
    streamId,
    postIds,
    strict,
  }: {
    streamId: PostStreamId;
    postIds: string[];
    strict: boolean;
  }): Promise<string[]> {
    if (postIds.length === 0 || !isAuthorScopedContentSearchStream(streamId)) {
      return postIds;
    }

    const relationships = await LocalPostService.readRelationshipsByIds(postIds);
    return postIds.filter((_postId, index) => {
      const relationship = relationships[index];
      if (!relationship) {
        return !strict;
      }
      return relationship.replied === null;
    });
  }

  /** Drop collections with zero items. Fail-open when local details are missing. */
  private static async filterEmptyCollections(postIds: string[]): Promise<string[]> {
    if (postIds.length === 0) return postIds;
    const details = await PostDetailsModel.findByIdsPreserveOrder(postIds);
    return postIds.filter((_id, index) => {
      const detail = details[index];
      if (!detail) return true;
      return (parseCollectionContent(detail.content)?.items?.length ?? 0) > 0;
    });
  }

  /**
   * Discover-only, id-based filters that need no post details: drop the viewer's own collections
   * and ones they have already bookmarked. Deterministic, so it runs once in the fetch loop and
   * never needs the post-hydration second pass.
   */
  static filterDiscoverOwnAndBookmarked(
    postIds: string[],
    viewerId: Pubky | null,
    bookmarkedIds: Set<string>,
  ): string[] {
    return postIds.filter((id) => {
      if (bookmarkedIds.has(id)) return false;
      if (viewerId) {
        // A malformed id parses to null and is treated as not-own (kept), so one bad id from
        // Nexus can't throw out of this filter and fail the whole fetch.
        let ownerPubky: Pubky | null = null;
        try {
          ownerPubky = parseCompositeId(id).pubky;
        } catch {
          ownerPubky = null;
        }
        if (ownerPubky === viewerId) return false;
      }
      return true;
    });
  }

  /**
   * Prepares the stream for initial load by performing cleanup operations.
   *
   * This method should be called before fetching the initial stream slice to ensure
   * the stream state is consistent. It performs the following operations:
   * 1. Clears the cache if the main head is older than the configured max age, or has no
   *    details: such a head can be neither aged nor polled from (#2608)
   * 2. Merges the unread posts into the main stream in polled order, from the first one whose
   *    details are cached and not a tombstone; the ids above it without details stay unread,
   *    since as the main head one of them would resolve no timestamp
   *    (`LocalStreamPostsService.markUnreadPostsAsReadFromResolvableHead`)
   * 3. Clears the merged posts and the tombstoned unread ids from the unread stream; with no
   *    cached main row, or an empty one, nothing is merged and the whole unread row is
   *    dropped, so the first page comes from Nexus
   *
   * This prevents race conditions where the StreamCoordinator might fetch posts
   * that are already in the main stream (due to stale unread stream head).
   *
   * @param streamId - The ID of the stream to prepare
   */
  static async prepareStreamForInitialLoad({ streamId }: TStreamIdParams): Promise<void> {
    // Initial loads and pull-to-refresh should start from the real stream head,
    // not reuse buffered overflow from a previous pagination session.
    postStreamQueue.remove(streamId);

    // 0. Rebuild streams invalidated by follow/friendship/profile-tag mutations.
    // Mutations only mark scopes dirty (mounted feeds are never disturbed);
    // the stale membership is dropped here, on the next initial load (#2294).
    if (postStreamDirtyRegistry.isDirty(streamId)) {
      Logger.debug('[PostStreamApplication] Stream marked dirty by a dependency mutation, clearing both streams', {
        streamId,
      });
      await Promise.all([
        LocalStreamPostsService.deleteById({ streamId }),
        LocalStreamPostsService.clearUnreadStream({ streamId }),
      ]);
      postStreamDirtyRegistry.markReconciled(streamId);
      return;
    }

    const now = Date.now();

    // 1. Check if main stream cache is stale. A head without details resolves no timestamp,
    // so the age check would keep the row forever while no poll can start from it (#2608):
    // rebuild it from Nexus like a stale one.
    const mainStreamHead = await this.getMainStreamHeadTimestamp({ streamId });
    if (mainStreamHead === SKIP_FETCH_NEW_POSTS || this.isTimestampStale(mainStreamHead, now)) {
      // Main cache is stale - clear both main stream and unread stream (both are outdated)
      Logger.debug('[PostStreamApplication] Main stream cache is stale, clearing both streams', {
        streamId,
        headTimestamp: mainStreamHead,
        ageMs: now - mainStreamHead,
        maxAgeMs: getStreamCacheMaxAgeMs(),
      });
      await Promise.all([
        LocalStreamPostsService.deleteById({ streamId }),
        LocalStreamPostsService.clearUnreadStream({ streamId }),
      ]);
      return;
    }

    // 2. Check if unread stream is stale before merging
    const unreadStreamHead = await this.getUnreadStreamHeadTimestamp({ streamId });
    if (this.isTimestampStale(unreadStreamHead, now)) {
      // Unread stream is stale - just clear it without merging
      Logger.debug('[PostStreamApplication] Unread stream is stale, clearing without merge', {
        streamId,
        headTimestamp: unreadStreamHead,
        ageMs: now - unreadStreamHead,
        maxAgeMs: getStreamCacheMaxAgeMs(),
      });
      await LocalStreamPostsService.clearUnreadStream({ streamId });
      return;
    }

    // 3. Both streams are fresh - merge and acknowledge the unread row from its first id that
    // can be the main head; ids without details above it stay unread for the poll-time retry
    // (#2608, see the service method).
    await LocalStreamPostsService.markUnreadPostsAsReadFromResolvableHead({ streamId });
  }

  /**
   * Check if a timestamp is stale (older than configured max age)
   * Returns false for sentinel values (0, 1) as they indicate empty/missing streams
   */
  private static isTimestampStale(timestamp: number, now: number): boolean {
    // Sentinel values indicate empty/missing streams - not stale
    if (timestamp === SKIP_FETCH_NEW_POSTS || timestamp === FORCE_FETCH_NEW_POSTS) {
      return false;
    }
    const ageMs = now - timestamp;
    return ageMs > getStreamCacheMaxAgeMs();
  }

  /**
   * Get the head timestamp of the main post stream only (not unread)
   */
  private static async getMainStreamHeadTimestamp({ streamId }: TStreamIdParams): Promise<number> {
    const postCompositeId = await PostStreamModel.getStreamHead(streamId);
    if (!postCompositeId) {
      return FORCE_FETCH_NEW_POSTS;
    }
    // Bookmark streams stale-check against bookmark time, not the (possibly old)
    // bookmarked post's `indexed_at` — otherwise bookmarking an old post would mark
    // the whole cache stale on every load and defeat local-first caching.
    if (isBookmarkStream(streamId)) {
      const bookmarkedAt = await this.getBookmarkTimestamp(postCompositeId as string);
      if (bookmarkedAt !== undefined) {
        return bookmarkedAt;
      }
    }
    const postDetails = await PostDetailsModel.findById(postCompositeId);
    return postDetails?.indexed_at ?? SKIP_FETCH_NEW_POSTS;
  }

  /**
   * Poll-time fallback: the timestamp of the first main-row id that resolves one (bookmark time
   * for bookmark streams). A main-row id without details, left at the head when the post above
   * it was removed, then stalls nothing: the poll runs from the id below it and lists it again
   * for the unread hydration to retry (#2608). `getMainStreamHeadTimestamp` stays literal so the
   * initial load still rebuilds a row whose head has no details.
   */
  private static async getResolvableMainStreamHeadTimestamp({ streamId }: TStreamIdParams): Promise<number> {
    const postStream = await PostStreamModel.findById(streamId);
    if (!postStream || postStream.stream.length === 0) {
      return FORCE_FETCH_NEW_POSTS;
    }
    for (const postCompositeId of postStream.stream) {
      const timestamp = await this.getStreamCursorTimestamp(streamId, postCompositeId);
      if (timestamp !== undefined) {
        return timestamp;
      }
    }
    return SKIP_FETCH_NEW_POSTS;
  }

  /**
   * Get the head timestamp of the unread stream only
   */
  private static async getUnreadStreamHeadTimestamp({ streamId }: TStreamIdParams): Promise<number> {
    const unreadCompositePostId = await UnreadPostStreamModel.getStreamHead(streamId);
    if (!unreadCompositePostId) {
      return FORCE_FETCH_NEW_POSTS;
    }
    if (isBookmarkStream(streamId)) {
      const bookmarkedAt = await this.getBookmarkTimestamp(unreadCompositePostId as string);
      if (bookmarkedAt !== undefined) {
        return bookmarkedAt;
      }
    }
    const postDetails = await PostDetailsModel.findById(unreadCompositePostId);
    return postDetails?.indexed_at ?? SKIP_FETCH_NEW_POSTS;
  }

  /**
   * Fetches a page of posts for a stream.
   * Filters out muted authors for most streams; skips filtering for author timelines and bookmarks.
   */
  static async getOrFetchStreamSlice({
    streamId,
    streamHead,
    streamTail,
    lastPostId,
    visiblePostIds,
    limit,
    viewerId,
    isCurrent,
    order,
  }: TFetchStreamParams): Promise<TPostStreamChunkResponse> {
    // Skip cache for ascending order (chronological) - always fetch from Nexus
    // This is because cache is stored in descending order
    // TODO: Might be a better way to handle this.
    if (order === StreamOrder.ASCENDING) {
      return await this.fetchStreamFromNexus({ streamId, limit, streamTail, streamHead, viewerId, isCurrent, order });
    }

    // Coordinator head-polls (streamHead > 0) only need the fetch side effects (unread
    // persist + counts + cache-miss ids); the response is discarded. Keep them out of the
    // shared pagination queue: routing them through collect() consumes/rewrites the UI's
    // overflow buffer, and with a raw resume anchor those buffered posts would be skipped.
    if (streamHead > SKIP_FETCH_NEW_POSTS) {
      return await this.fetchStreamFromNexus({ streamId, limit, streamTail, streamHead, viewerId, isCurrent, order });
    }

    // Author streams and bookmarks intentionally include posts from muted users:
    // bookmarks are explicit saves (#1804); profile is someone's full timeline.
    const shouldFilterMuted = !isAuthorStreamSkippingMuteFilter(streamId) && !isBookmarkStream(streamId);
    const mutedUserIds = shouldFilterMuted
      ? new Set((await LocalStreamUsersService.findById(UserStreamTypes.MUTED))?.stream ?? [])
      : new Set<Pubky>();

    // Discover Collections filters out the viewer's own and already-bookmarked collections in the
    // shared stream layer (like mute/deleted), so the section paginates like any other feed.
    const isDiscover = isDiscoverCollectionsStream(streamId);
    const bookmarkedIds = isDiscover ? new Set(await BookmarkModel.findAll()) : new Set<string>();

    let lastReturnedPostId: string | undefined = lastPostId;

    const { posts, cacheMissIds, nextCursor, reachedEnd, rawScannedCount } = await postStreamQueue.collect(streamId, {
      limit,
      cursor: streamTail,
      // Discover bounds its per-load scan tighter than the shared default: it is a
      // secondary surface, so we cap the data/latency cost of digging through a
      // heavily-filtered region and let the no-new-results toast + cursor advance
      // handle the give-up case instead.
      maxIterations: isDiscover ? COLLECTIONS_DISCOVER_MAX_FETCHES_PER_LOAD : undefined,
      filter: async (posts) => {
        // Muted users (sync), then deleted/kind/empty (async), then Discover's own/bookmarked.
        const afterMuteFilter = MuteFilter.filterPosts(posts, mutedUserIds);
        const standard = await PostStreamApplication.filterStreamPosts({ streamId, postIds: afterMuteFilter });
        return isDiscover
          ? PostStreamApplication.filterDiscoverOwnAndBookmarked(standard, viewerId, bookmarkedIds)
          : standard;
      },
      fetch: async (cursor) => {
        // Continue reading from cache using lastReturnedPostId to track position
        // This ensures we exhaust cache before going to Nexus
        const result = await this.fetchStreamSliceInternal({
          streamId,
          // Head-polls (streamHead > 0) bypass collect() above, so every fetch here
          // is a pagination read below the head.
          streamHead: SKIP_FETCH_NEW_POSTS,
          streamTail: cursor,
          lastPostId: lastReturnedPostId,
          visiblePostIds,
          limit,
          viewerId,
          isCurrent,
          order,
        });

        // Track the raw position for cache continuation: the anchor the fetch positioned in
        // the row after a Nexus page, else the last id of a cache chunk.
        if (result.lastRawPostId !== undefined) {
          lastReturnedPostId = result.lastRawPostId;
        } else if (result.nextPageIds.length > 0) {
          lastReturnedPostId = result.nextPageIds[result.nextPageIds.length - 1];
        }

        return result;
      },
    });

    // Fetch original posts for any reposts served from cache
    // (handles case where repost is cached but original was evicted)
    try {
      const relationships = await LocalPostService.readRelationshipsByIds(posts);
      const repostedUris = relationships
        .filter((rel): rel is PostRelationshipsModelSchema => rel !== undefined && rel.reposted !== null)
        .map((rel) => rel.reposted as string);
      await this.fetchOriginalPostsByUris({ repostedUris, viewerId, isCurrent });
    } catch (error) {
      Logger.warn('Failed to fetch missing repost content', { postIds: posts, error });
    }

    return {
      nextPageIds: posts,
      cacheMissPostIds: cacheMissIds,
      nextCursor,
      reachedEnd,
      // The raw cache-walk anchor: last raw id scanned this round (buffer-only rounds
      // scan nothing, so it holds at the caller's own lastPostId).
      lastRawPostId: lastReturnedPostId,
      rawScannedCount,
    };
  }

  static async fetchStreamSlice(params: TFetchStreamParams): Promise<TPostStreamChunkResponse> {
    return await this.fetchStreamFromNexus(params);
  }

  // ============================================================================
  // Internal Helpers
  // ============================================================================

  /**
   * Gets the indexed_at timestamp from a post for pagination cursor advancement.
   * Returns undefined if the post details cannot be found.
   */
  private static async getPostTimestamp(postId: string): Promise<number | undefined> {
    try {
      const postDetails = await LocalPostService.readDetails({ postId });
      return postDetails?.indexed_at;
    } catch (error) {
      Logger.warn('Failed to get post timestamp', { postId, error });
      return undefined;
    }
  }

  /**
   * Bookmark time (`created_at`) for a post, or undefined if it isn't bookmarked
   * locally. Bookmark streams are ordered by this on Nexus — not the post's own
   * `indexed_at` — so it is the correct key for both pagination cursors and the
   * stream-head staleness check.
   */
  private static async getBookmarkTimestamp(postId: string): Promise<number | undefined> {
    try {
      const bookmark = await BookmarkModel.findById(postId);
      return typeof bookmark?.created_at === 'number' ? bookmark.created_at : undefined;
    } catch (error) {
      Logger.warn('Failed to get bookmark timestamp', { postId, error });
      return undefined;
    }
  }

  /**
   * Resolve the pagination cursor timestamp for a post within a given stream.
   *
   * Bookmark streams must page by bookmark time (see `getBookmarkTimestamp`) rather
   * than the post's `indexed_at`. Seeding the cursor from the post timestamp desyncs
   * the cache→Nexus pagination seam — a post bookmarked recently but created long ago
   * would send an old `start`, skipping or repeating bookmarks. Every other stream
   * paginates by the post's `indexed_at`.
   *
   * Falls open to the post timestamp if the bookmark row is missing.
   */
  private static async getStreamCursorTimestamp(streamId: PostStreamId, postId: string): Promise<number | undefined> {
    if (isBookmarkStream(streamId)) {
      const bookmarkedAt = await this.getBookmarkTimestamp(postId);
      if (bookmarkedAt !== undefined) {
        return bookmarkedAt;
      }
    }
    return this.getPostTimestamp(postId);
  }

  /**
   * Internal method that performs the actual fetch without mute filtering.
   * This is the original getOrFetchStreamSlice logic, now used as a building block.
   */
  private static async fetchStreamSliceInternal({
    streamId,
    streamHead,
    streamTail,
    lastPostId,
    visiblePostIds,
    limit,
    viewerId,
    isCurrent,
    order,
  }: TFetchStreamParams): Promise<TPostStreamChunkResponse> {
    // Avoid the indexdb query for skip-paginated streams (engagement + single-collection items):
    // their local cache is timestamp-keyed and incompatible with offset pagination.
    let seededFromTimestamp = false;
    if (!isSkipPaginatedStream(streamId) && !streamHead) {
      const cachedStream = await LocalStreamPostsService.read({ streamId });

      if (cachedStream && cachedStream.stream.length > 0) {
        const cachedStreamChunk = await this.getStreamFromCache({ lastPostId, visiblePostIds, limit, cachedStream });
        // Every cache→Nexus seam resumes from the row's own Nexus cursor — never from the
        // timestamp of a post in the chunk (see `resolveCachedStreamTailCursor`).
        const tailCursor = await this.resolveCachedStreamTailCursor(streamId, cachedStream);
        // A row without a cursor seeds once from a post timestamp, which can overshoot the
        // row if that post was edited; the seam page is then trimmed to the ids below the row.
        seededFromTimestamp = cachedStream.tailCursor === undefined;

        // Full cache hit: the caller keeps walking the cache by id; the cursor it carries is
        // the position Nexus continues from once the cache runs out.
        if (cachedStreamChunk.length === limit) {
          return { nextPageIds: cachedStreamChunk, cacheMissPostIds: [], nextCursor: tailCursor, reachedEnd: false };
        }

        // Partial cache hit, fetch missing posts from Nexus and combine
        if (cachedStreamChunk.length > 0) {
          return await this.partialCacheHit({
            cachedStreamChunk,
            limit,
            streamTail: tailCursor ?? streamTail,
            seededFromTimestamp,
            streamId,
            viewerId,
            isCurrent,
          });
        }

        // Cache exhausted (the walk is at the row tail): continue below the deepest page
        // fetched into it. A lost anchor never lands here — `getStreamFromCache` re-anchors
        // it — so this cannot skip cached ids the caller has not seen.
        if (lastPostId && tailCursor !== undefined) {
          streamTail = tailCursor;
        }
      }

      // Defensive check: If this is an initial load (no lastPostId) and cache doesn't exist or is empty,
      // force fetching from the beginning by setting streamTail to 0.
      // This ensures correctness even if a non-zero streamTail was incorrectly passed for an initial load.
      if (!lastPostId && (!cachedStream || cachedStream.stream.length === 0)) {
        streamTail = NOT_FOUND_CACHED_STREAM;
      }
    }
    return await this.fetchStreamFromNexus({
      streamId,
      limit,
      streamTail,
      streamHead,
      lastPostId,
      seededFromTimestamp,
      viewerId,
      isCurrent,
      order,
    });
  }

  /**
   * Fetch missing posts from nexus and persist them to cache
   * @param cacheMissPostIds - Array of post IDs that are not persisted in cache
   * @param viewerId - ID of the viewer
   * @param streamHead - Detects if the call is coming from the streamCoordinator.
   * @param streamId - ID of the stream. If not provided, it means that it is a single post operation.
   * @returns Whether hydration succeeded. Failure is non-fatal for fail-open streams
   * (they render what the cache has), but callers with strict post-hydration
   * filtering (author-scoped content search) must not mistake it for "no results".
   */
  static async fetchMissingPostsFromNexus({
    cacheMissPostIds,
    viewerId,
    isCurrent,
    force,
  }: TMissingPostsParams): Promise<boolean> {
    try {
      if (isCurrent && !isCurrent()) return false;
      const revisions = await LocalTagCacheService.captureRevisions('post', cacheMissPostIds);
      const postBatch = await NexusPostStreamService.fetchByIds({
        post_ids: cacheMissPostIds,
        force,
        // Only pass viewer_id if it's a valid string (not null/undefined)
        ...(viewerId ? { viewer_id: viewerId } : {}),
      });
      if (isCurrent && !isCurrent()) return false;
      // Keep missing posts retryable until their attachment metadata is durable.
      await FileApplication.persistFiles(postBatch.flatMap((post) => post.attachments_metadata ?? []));
      if (isCurrent && !isCurrent()) return false;
      await LocalStreamPostsService.persistPosts({
        posts: postBatch,
        tagGuard: { revisions, isCurrent, viewerId },
      });
      // Persist the missing authors of the posts
      await this.fetchMissingPostAuthors({ posts: postBatch, viewerId, isCurrent });
      // Fetch original posts for any reposts (to display embedded repost content)
      const repostedUris = postBatch
        .map((post) => post.relationships.reposted)
        .filter((uri): uri is string => uri !== null);
      await this.fetchOriginalPostsByUris({ repostedUris, viewerId, isCurrent });
      return true;
    } catch (error) {
      Logger.warn('Failed to fetch missing posts from Nexus', { cacheMissPostIds, viewerId, error });
      return false;
    }
  }

  /**
   * Shared logic for fetching original posts by their URIs.
   * Converts URIs to IDs, checks cache, fetches missing posts from Nexus, and persists them.
   * This method is public to allow reuse by TtlApplication for refreshing repost originals.
   * @param repostedUris - Array of pubky URIs pointing to original posts
   * @param viewerId - ID of the viewer
   */
  static async fetchOriginalPostsByUris({
    repostedUris,
    viewerId,
    isCurrent,
  }: {
    isCurrent?: () => boolean;
    repostedUris: string[];
    /** Optional viewer ID for relationship data. Null/undefined for unauthenticated views. */
    viewerId?: Pubky | null;
  }) {
    if (repostedUris.length === 0) return;

    // Convert URIs to composite IDs and deduplicate
    const originalPostIds = Array.from(
      new Set(
        repostedUris
          .map((uri) =>
            buildCompositeIdFromPubkyUri({
              uri,
              domain: CompositeIdDomain.POSTS,
            }),
          )
          .filter((id): id is string => id !== null),
      ),
    );

    if (originalPostIds.length === 0) return;

    // Filter out posts already in local DB
    const missingOriginalPostIds = await LocalStreamPostsService.getNotPersistedPostsInCache(originalPostIds);

    if (missingOriginalPostIds.length === 0) return;

    Logger.debug('Fetching original posts for reposts', {
      repostCount: repostedUris.length,
      originalCount: originalPostIds.length,
      missingOriginalCount: missingOriginalPostIds.length,
    });

    try {
      if (isCurrent && !isCurrent()) return;
      const revisions = await LocalTagCacheService.captureRevisions('post', missingOriginalPostIds);
      const originalPosts = await NexusPostStreamService.fetchByIds({
        post_ids: missingOriginalPostIds,
        viewer_id: viewerId ?? undefined,
      });
      if (isCurrent && !isCurrent()) return;
      // Do not cache an original post as hydrated before its attachments are stored.
      await FileApplication.persistFiles(originalPosts.flatMap((post) => post.attachments_metadata ?? []));
      if (isCurrent && !isCurrent()) return;
      await LocalStreamPostsService.persistPosts({
        posts: originalPosts,
        tagGuard: { revisions, isCurrent, viewerId },
      });
      await this.fetchMissingPostAuthors({ posts: originalPosts, viewerId, isCurrent });
    } catch (error) {
      Logger.warn('Failed to fetch original posts for reposts', { missingOriginalPostIds, error });
    }
  }

  /**
   * Handles partial cache hits by fetching remaining posts from Nexus and combining with cached posts.
   *
   * @param cachedStreamChunk - Array of post IDs from cache that need to be combined with fetched posts
   * @param limit - Maximum number of posts to return
   * @param streamTail - The cached stream's Nexus resume cursor (`resolveCachedStreamTailCursor`)
   * @param streamId - ID of the post stream
   * @param viewerId - ID of the viewer
   **/
  private static async partialCacheHit({
    cachedStreamChunk,
    limit,
    streamTail,
    seededFromTimestamp,
    streamId,
    viewerId,
    isCurrent,
  }: TPartialCacheHitParams): Promise<TPostStreamChunkResponse> {
    const lastCachedPostId = cachedStreamChunk[cachedStreamChunk.length - 1];
    const remainingLimit = limit - cachedStreamChunk.length;

    // Fetch remaining posts from Nexus, resuming below the deepest page already cached
    const { nextPageIds, cacheMissPostIds, nextCursor, reachedEnd, lastRawPostId } = await this.fetchStreamFromNexus({
      streamId,
      limit: remainingLimit,
      streamTail,
      streamHead: SKIP_FETCH_NEW_POSTS,
      seededFromTimestamp,
      viewerId,
      isCurrent,
      lastPostId: lastCachedPostId,
    });

    // Combine cached posts with fetched posts, deduplicating
    const uniquePostIds = Array.from(new Set([...cachedStreamChunk, ...nextPageIds]));

    return {
      nextPageIds: uniquePostIds,
      cacheMissPostIds,
      nextCursor,
      // Propagate reachedEnd from Nexus - don't recalculate from deduped length
      reachedEnd: reachedEnd ?? false,
      // The walk resumes from the anchor the fetch positioned in the row after the page was
      // appended (the last cached id when the page was empty), never from the chunk end.
      lastRawPostId: lastRawPostId ?? lastCachedPostId,
    };
  }

  private static async fetchMissingPostAuthors({ posts, viewerId, isCurrent }: TFetchMissingUsersParams) {
    const cacheMissUserIds = await this.getNotPersistedUsersInCache(
      posts.map((post) => post.details.author),
      viewerId ?? undefined,
    );
    if (cacheMissUserIds.length > 0) {
      if (isCurrent && !isCurrent()) return;
      const fetchStartedAt = Date.now();
      const revisions = await LocalTagCacheService.captureRevisions('user', cacheMissUserIds);
      const userBatch = await NexusUserStreamService.fetchByIds({
        user_ids: cacheMissUserIds,
        viewer_id: viewerId ?? undefined,
      });
      await LocalStreamUsersService.persistUsers(userBatch, { revisions, isCurrent, viewerId, fetchStartedAt });
    }
  }

  private static async fetchStreamFromNexus({
    streamId,
    limit,
    streamHead,
    streamTail,
    lastPostId,
    seededFromTimestamp,
    viewerId,
    isCurrent,
    order,
  }: TFetchStreamParams): Promise<TPostStreamChunkResponse> {
    // Nexus bounds the by_content offset (skip ≤ CONTENT_SEARCH_MAX_SKIP, inclusive). A cursor
    // already past the bound could only produce a request Nexus rejects — report the end
    // defensively instead of issuing it.
    if (isContentSearchStream(streamId) && streamTail > CONTENT_SEARCH_MAX_SKIP) {
      return { nextPageIds: [], cacheMissPostIds: [], nextCursor: undefined, reachedEnd: true };
    }

    const { params, invokeEndpoint, extraParams } = createPostStreamParams({
      streamId,
      streamTail,
      limit,
      streamHead,
      viewerId,
      order,
    });
    const postStreamChunk = await NexusPostStreamService.fetch({ invokeEndpoint, params, extraParams });
    if (isCurrent && !isCurrent())
      return { nextPageIds: [], cacheMissPostIds: [], nextCursor: undefined, reachedEnd: false };
    // `last_post_score` is null for skip streams; normalize to undefined (advanceCursor derives
    // their offset from the raw page instead).
    const { last_post_score: rawScore, post_keys: rawPageIds } = postStreamChunk;
    // A cursor seeded from a post timestamp can start inside or above the cached row (the
    // post was edited after newer posts arrived). Only the part of the page below the row is
    // served or cached; the rest is cached already or belongs to a head poll. The page's own
    // cursor still moves the walk on, so a page inside the row is skipped, not re-requested.
    const alignsWithRow = seededFromTimestamp && !isSkipPaginatedStream(streamId) && order !== StreamOrder.ASCENDING;
    const compositePostIds = alignsWithRow
      ? await LocalStreamPostsService.keepIdsBelowRow({ streamId, stream: rawPageIds })
      : rawPageIds;
    let cacheMissPostIds = await this.getCacheMissPostIds(streamId, compositePostIds);
    // Reply hooks observe stream IDs directly. Hydrate first so newly mounted cards
    // find details/counts/tags locally instead of racing the batch with individual fetches.
    if (invokeEndpoint === StreamSource.REPLIES && streamHead === SKIP_FETCH_NEW_POSTS && cacheMissPostIds.length > 0) {
      const hydrated = await this.fetchMissingPostsFromNexus({ cacheMissPostIds, viewerId, isCurrent });
      if (hydrated) cacheMissPostIds = [];
    }

    if (isCurrent && !isCurrent())
      return { nextPageIds: [], cacheMissPostIds: [], nextCursor: undefined, reachedEnd: false };
    // Do not persist skip-paginated streams (engagement + single-collection items) to the
    // timestamp-keyed local stream cache; they always page from Nexus by offset.
    let persistedRow: string[] | undefined;
    if (!isSkipPaginatedStream(streamId) && streamHead === SKIP_FETCH_NEW_POSTS) {
      persistedRow = await LocalStreamPostsService.persistNewStreamChunk({
        // Reply rows are cached newest-first. An ascending page is handed over reversed so
        // that ids still missing their details (a hydration that failed) keep their relative
        // order under the timestamp sort, which interpolates assuming descending input.
        stream: order === StreamOrder.ASCENDING ? [...compositePostIds].reverse() : compositePostIds,
        streamId,
        // A descending page extends the cached stream downward: record Nexus's own position
        // as the row's resume cursor. Ascending pages read a reply thread upward from its
        // oldest cached reply and carry no tail position.
        tailCursor: order === StreamOrder.ASCENDING ? undefined : (rawScore ?? undefined),
      });
    }

    // When streamHead is greater than 0, it means that it is a streamCoordinator calling this method.
    // In the future, we might need to add some enum param to describe that type of call.
    // For now, that kind of queries comes from the streamCoordinator.
    if (streamHead > SKIP_FETCH_NEW_POSTS) {
      await this.persistUnreadStreamChunkAndUpdateCounts({
        streamId,
        compositePostIds,
      });
    }

    // reachedEnd is true when Nexus returned fewer posts than requested (actual end of stream).
    // Content search also ends when the NEXT offset (this skip + raw ids consumed) would
    // overflow Nexus's inclusive skip bound: this valid page is still consumed, only the
    // follow-up request is suppressed. A next offset landing exactly on the bound stays valid.
    const overflowsContentSearchSkip =
      isContentSearchStream(streamId) && streamTail + rawPageIds.length > CONTENT_SEARCH_MAX_SKIP;

    return {
      nextPageIds: compositePostIds,
      cacheMissPostIds,
      nextCursor: rawScore ?? undefined,
      reachedEnd: rawPageIds.length < limit || overflowsContentSearchSkip,
      // The cache walk resumes from the deepest of this page's ids and the caller's anchor,
      // as positioned in the row after the page was persisted: a page the row already held
      // (a cursor-less row seeded above its tail) must not drag the anchor back up the row,
      // and a row another walker extended past this page must not let the anchor jump over
      // ids this walker never served.
      lastRawPostId:
        order === StreamOrder.ASCENDING ? undefined : this.resumeAnchorAfterPage(persistedRow, rawPageIds, lastPostId),
    };
  }

  // Delegate to service for cache miss detection
  private static async getNotPersistedPostsInCache(postIds: string[]): Promise<string[]> {
    return LocalStreamPostsService.getNotPersistedPostsInCache(postIds);
  }

  /**
   * Cache-miss detection for a fetched stream page. Author-scoped content search additionally
   * treats a missing relationships row as a miss: reply exclusion classifies via
   * `relationships.replied`, so a details-cached post without one must still be hydrated —
   * otherwise it would stay fail-open (or be dropped by the strict second pass) forever.
   */
  private static async getCacheMissPostIds(streamId: PostStreamId, postIds: string[]): Promise<string[]> {
    const missingDetailsIds = await this.getNotPersistedPostsInCache(postIds);
    if (postIds.length === 0 || !isAuthorScopedContentSearchStream(streamId)) {
      return missingDetailsIds;
    }

    const [details, relationships] = await Promise.all([
      LocalPostService.readDetailsByIds(postIds),
      LocalPostService.readRelationshipsByIds(postIds),
    ]);
    // A locally tombstoned post keeps its details row but its relationships row is gone
    // for good: re-fetching can never classify it, and the deleted filter drops it
    // anyway, so flagging it would issue a futile by_ids request on every page load.
    const missingRelationshipsIds = postIds.filter(
      (_postId, index) => !relationships[index] && details[index]?.content !== DELETED,
    );
    return Array.from(new Set([...missingDetailsIds, ...missingRelationshipsIds]));
  }

  private static async filterCollectionsFromStream({
    streamId,
    postIds,
  }: {
    streamId: PostStreamId;
    postIds: string[];
  }): Promise<string[]> {
    if (!this.shouldExcludeCollectionsFromStream(streamId) || postIds.length === 0) {
      return postIds;
    }

    const postDetails = await LocalPostService.readDetailsByIds(postIds);
    return postIds.filter((_postId, index) => postDetails[index]?.kind !== StreamKind.COLLECTION);
  }

  /** Collections appear only on streams whose id encodes `kind=collection` (e.g. timeline:all:collection). */
  private static shouldExcludeCollectionsFromStream(streamId: PostStreamId): boolean {
    // Profile search inherits the Posts tab contract: collections have their own tab.
    if (isAuthorScopedContentSearchStream(streamId)) {
      return true;
    }

    // Content search is the one family where `kind=all` means "including
    // collections". Narrower kinds (image, video, …) fall through and keep the
    // local collection filter as defense-in-depth, like every other family.
    if (isContentSearchStream(streamId) && parseContentSearchStreamId(streamId)?.kind === 'all') {
      return false;
    }

    const { kind } = breakDownStreamId(streamId);
    return kind !== StreamKind.COLLECTION;
  }

  /**
   * Persist the unread stream chunk and update the counts of the posts and users
   * @param streamId - The ID of the stream
   * @param compositePostIds - The new posts IDs that are going to be persisted in the unreadstream
   */
  private static async persistUnreadStreamChunkAndUpdateCounts({
    streamId,
    compositePostIds,
  }: TPersistUnreadNewStreamChunkParams) {
    const newToUnreadStream = await LocalStreamPostsService.persistUnreadNewStreamChunk({
      stream: compositePostIds,
      streamId,
    });

    // Skip count updates if no new posts were added to unread stream
    if (newToUnreadStream.length === 0) return;

    // Filter out posts that already exist in the database (e.g., locally created posts).
    // These posts have already been counted when they were created, so we should not
    // increment counts again when they arrive via the unread stream.
    const notInDatabase = await this.getNotPersistedPostsInCache(newToUnreadStream);

    // Skip count updates if all posts already exist in the database
    if (notInDatabase.length === 0) return;

    // Also filter out posts that are already in the main post stream for this streamId.
    // This catches locally created posts that were added to the stream but might not
    // have been committed to PostDetailsModel yet due to transaction timing.
    const existingStream = await LocalStreamPostsService.read({ streamId });
    const existingStreamIds = new Set(existingStream?.stream ?? []);
    const trulyNewPostIds = notInDatabase.filter((id) => !existingStreamIds.has(id));

    // Skip count updates if all posts are already in the stream
    if (trulyNewPostIds.length === 0) return;

    // The authorId and postId are going to be use to identify the replies parent id
    const { sorting: replyParentAuthorId, invokeEndpoint, kind: replyParentPostId } = breakDownStreamId(streamId);

    // If it is a reply, we need to update the parent post counts
    // TODO: Might happen some edge cases but for now, we can go with this approach.
    if (invokeEndpoint === StreamSource.REPLIES) {
      const replyParentPostCompositeId = buildCompositeId({
        pubky: replyParentAuthorId,
        id: replyParentPostId as string,
      });
      await LocalPostService.updatePostCounts({
        postCompositeId: replyParentPostCompositeId,
        countChanges: { replies: trulyNewPostIds.length },
      });
    }

    // NOTE: We intentionally do NOT optimistically bump the post authors' user
    // `posts`/`replies` counts from streamed-in posts. That bump was unlinked from
    // Nexus — it had no reconciliation against when each author's count was last
    // fetched — so it double-counted and could show counts higher than reality,
    // which the TTL refetch then snapped back down. The authoritative counts come
    // from `fetchCounts` (TTL), and a user's own actions are still reflected
    // instantly via the mutation paths (create/delete/follow/bookmark).
  }

  // Delegate to service for cache miss detection
  private static async getNotPersistedUsersInCache(userIds: Pubky[], viewerId?: Pubky): Promise<Pubky[]> {
    return LocalStreamUsersService.getNotPersistedUsersInCache([...new Set(userIds)], viewerId);
  }

  private static async getStreamFromCache({
    lastPostId,
    visiblePostIds,
    limit,
    cachedStream,
  }: TCacheStreamParams): Promise<string[]> {
    // Handle limit 0 case, return empty array immediately
    if (limit === 0) {
      return [];
    }

    // If the lastPostId is not provided, it means that we are in the head of the stream
    if (!lastPostId) {
      // Return all available posts from cache (up to limit)
      // If cache has fewer posts than limit, return what's available
      return cachedStream.stream.slice(0, Math.min(limit, cachedStream.stream.length));
    }

    // lastPostId is provided, find the position in cache
    let postIndex = cachedStream.stream.indexOf(lastPostId);
    if (postIndex === -1) {
      // The anchor was removed from the row (its post deleted or un-bookmarked). Resume
      // after the deepest id the caller has already rendered, so at most the raw tail of
      // one page is re-served; with no such id, re-walk from the head and let the caller
      // dedupe. Jumping to the row tail instead would skip every cached id in between.
      postIndex = this.deepestCachedIndex(cachedStream.stream, visiblePostIds);
      if (postIndex === -1) {
        return cachedStream.stream.slice(0, Math.min(limit, cachedStream.stream.length));
      }
    }

    // Return all available posts after lastPostId (up to limit)
    // If cache has fewer posts than requested, return what's available
    const startIndex = postIndex + 1;
    const endIndex = Math.min(startIndex + limit, cachedStream.stream.length);

    if (startIndex >= cachedStream.stream.length) {
      // No posts after lastPostId in cache
      return [];
    }

    return cachedStream.stream.slice(startIndex, endIndex);
  }

  /** The candidate (page id or previous anchor) that sits deepest in `row`; the row tail when none does. */
  private static resumeAnchorAfterPage(
    row: string[] | undefined,
    pageIds: string[],
    previousAnchor: string | undefined,
  ): string | undefined {
    if (!row || row.length === 0) return undefined;
    const index = this.deepestCachedIndex(row, previousAnchor === undefined ? pageIds : [...pageIds, previousAnchor]);
    return index === -1 ? row[row.length - 1] : row[index];
  }

  /** Highest row index among `candidates` that is present in `stream`, or -1. */
  private static deepestCachedIndex(stream: string[], candidates: string[] | undefined): number {
    if (!candidates || candidates.length === 0) return -1;
    const rowIndex = new Map(stream.map((id, index) => [id, index] as const));
    let deepest = -1;
    for (const id of candidates) {
      const index = rowIndex.get(id);
      if (index !== undefined && index > deepest) deepest = index;
    }
    return deepest;
  }
}
